import {
  createError,
  useAsyncData,
  useNuxtApp,
  useRequestURL,
  useRoute,
  useRuntimeConfig,
} from '#imports'
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import type { _ApiBlock } from '@backmarket/http-api/src/api-specs-content/models/block'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { isClient } from '@backmarket/nuxt-module-tracking/isClient'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { watchAtMost } from '@vueuse/core'

import type {
  EmergencyBannerFallback,
  PromotionalBanner,
} from '../../api/content/promotional-banner.types'
import { buildSimpleRichText } from '../../blocks/RichText/build-simple-rich-text'
import { buildPropsWithCorrectLinks } from '../../utils/buildPropsWithCorrectLinks'

export function useFetchPromotionalBanner() {
  const $httpFetch = useHttpFetch()
  const logger = useLogger()
  const currentUrl = useRequestURL()
  const route = useRoute()
  const currentLocale = useI18nLocale()
  const nuxtApp = useNuxtApp()
  const {
    public: { CMS_EMERGENCY_BANNER_FALLBACK_BASE_URL: fallbackBaseURL },
  } = useRuntimeConfig()

  const pageFinishedRendering = ref(false)
  const clientFetchedData = ref<PromotionalBanner | null>(null)
  const routeChanged = ref(false)

  async function fetchPromotionalBanner() {
    return $httpFetch('/content/components/:componentType/:id', {
      pathParams: {
        componentType: 'promotionalBannerComponent',
        id: 'promotional-banner',
      },
      timeout: 2000,
    })
  }

  async function fetchEmergencyBannerFallback() {
    if (!fallbackBaseURL || fallbackBaseURL === 'unknown') {
      throw createError({
        statusCode: 500,
        statusMessage: 'Missing fallback base URL',
      })
    }

    return $httpFetch('/emergency-fallback.json', {
      baseURL: fallbackBaseURL,
      credentials: 'omit',
      timeout: 2000,
    })
  }

  function parseEmergencyBannerContent(
    content: EmergencyBannerFallback,
  ): PromotionalBanner | null {
    const emergencyMessage = content?.[currentLocale]
    if (!emergencyMessage) return null

    return {
      blockList3: [
        {
          type: 'RichText',
          props: { richText: buildSimpleRichText(emergencyMessage, 'p') },
          hiddenDevices: [],
          id: 'emergency-text-from-bucket',
        },
      ],
    }
  }

  function cleanBannerContent(promotionalBanner: PromotionalBanner) {
    function filterAndBuild(richTextBlocks?: _ApiBlock<'RichText'>[]) {
      if (!richTextBlocks) return undefined

      return richTextBlocks
        .filter((block) => !isEmpty(block.props.richText))
        .map((block) => {
          return {
            ...block,
            props: buildPropsWithCorrectLinks({
              props: block.props,
              currentUrl,
            }),
          }
        })
    }

    return {
      ...promotionalBanner,
      blockList1: filterAndBuild(promotionalBanner.blockList1),
      blockList2: filterAndBuild(promotionalBanner.blockList2),
      blockList3: filterAndBuild(promotionalBanner.blockList3),
    }
  }

  const { data, execute } = useAsyncData<PromotionalBanner | null>(
    'cms-promotional-banner',
    async () => {
      try {
        const bannerResponse = await nuxtApp.runWithContext(() =>
          fetchPromotionalBanner(),
        )

        return cleanBannerContent(bannerResponse)
      } catch (promotionalBannerError) {
        const promotionalHttpError = HttpApiError.fromAnyError(
          promotionalBannerError,
        )
        logger.error(
          '[PromotionalBanner][SSR] Failed to fetch PromotionalBanner',
          { error: promotionalHttpError, owners: ['bot-squad-content-front'] },
        )

        try {
          const fallbackResponse = await nuxtApp.runWithContext(() =>
            fetchEmergencyBannerFallback(),
          )

          return parseEmergencyBannerContent(fallbackResponse)
        } catch (fallbackError) {
          const fallbackHttpError = HttpApiError.fromAnyError(fallbackError)

          logger.error('[EmergencyBanner][SSR] Failed to fetch fallback', {
            error: fallbackHttpError,
            owners: ['bot-squad-content-front'],
          })
        }
      }

      return null
    },
    { immediate: !isClient() },
  )

  // used when we navigate during CSR from a page that doesn't have the header
  // to a page that does, ex: shopping cart pages.
  if (isClient() && !nuxtApp.isHydrating) {
    void execute()

    pageFinishedRendering.value = true
  }

  // Assign the client side (background) fetched banner only during client navigation as this avoids a CLS impact.
  watchAtMost(
    () => route.fullPath,
    () => {
      if (!clientFetchedData.value) return

      routeChanged.value = true
    },
    { count: 1 },
  )

  /**
   * Used instead of onMounted() as it executes once the route navigation
   * is fully completed & all middleware has resolved.
   * -> page is fully ready, onMounted() waits for the component DOM to be ready only.
   */
  nuxtApp.hook('page:finish', async () => {
    if (pageFinishedRendering.value) return
    pageFinishedRendering.value = true

    try {
      const bannerResponse = await fetchPromotionalBanner()
      clientFetchedData.value = cleanBannerContent(bannerResponse)
    } catch (fetchError) {
      const httpError = HttpApiError.fromAnyError(fetchError)

      logger.error(
        '[PromotionalBanner][Client] Failed to fetch PromotionalBanner',
        { error: httpError, owners: ['bot-squad-content-front'] },
      )

      try {
        const fallbackResponse = await fetchEmergencyBannerFallback()
        clientFetchedData.value = parseEmergencyBannerContent(fallbackResponse)
      } catch (fallbackError) {
        const fallbackHttpError = HttpApiError.fromAnyError(fallbackError)

        logger.error('[EmergencyBanner][Client] Failed to fetch fallback', {
          error: fallbackHttpError,
          owners: ['bot-squad-content-front'],
        })
      }
    }
  })

  const bannerData = computed(() => {
    // if the banner is already rendered, return the client fetch text
    if (data.value && clientFetchedData.value) {
      return clientFetchedData.value
    }

    return routeChanged.value && clientFetchedData.value
      ? clientFetchedData.value
      : data.value
  })

  return { data: bannerData }
}
